import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Chat } from "../types/chat";
import { orchestratorApi } from "../api/orchestrator";
import { normalizePhoneNumber, formatPhoneNumber } from "../utils/phoneNormalization";
import { AuthedMedia, downloadAuthedMedia } from "./AuthedMedia";

// Multi-tenant SaaS: Auth0 namespace for custom claims
const NAMESPACE = import.meta.env.VITE_AUTH0_NAMESPACE || "https://whatsapp.entrustfi.io";

// Helper function to detect if text is likely English
const isLikelyEnglish = (text: string): boolean => {
  const trimmed = text.trim();

  // Empty or very short - assume English
  if (trimmed.length < 3) return true;

  // Count ASCII vs non-ASCII characters
  const asciiCount = (trimmed.match(/[\x00-\x7F]/g) || []).length;
  const nonAsciiCount = trimmed.length - asciiCount;

  // If >30% non-ASCII characters, likely non-English
  if (nonAsciiCount / trimmed.length > 0.3) {
    return false;
  }

  // Check for common non-Latin scripts (Chinese, Japanese, Korean, Arabic, Thai, etc.)
  const nonLatinScripts = /[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF\u0600-\u06FF\u0E00-\u0E7F]/;
  if (nonLatinScripts.test(trimmed)) {
    return false;
  }

  // Common English words (very cheap check)
  const commonEnglishWords = /\b(the|is|are|was|were|be|been|have|has|had|do|does|did|will|would|could|should|may|might|can|must|shall|of|to|in|for|on|at|by|with|from|as|this|that|these|those|and|or|but|not|no|yes|ok|thanks|thank|you|me|my|your|our|their|we|they|he|she|it|am|what|when|where|why|how|who|which)\b/i;

  const words = trimmed.split(/\s+/);
  const englishWordCount = words.filter(word => commonEnglishWords.test(word)).length;

  // If >30% of words are common English words, likely English
  if (englishWordCount / words.length > 0.3) {
    return true;
  }

  // Default: If mostly ASCII and no non-Latin scripts, assume English
  return nonAsciiCount / trimmed.length < 0.1;
};

// Helper functions for media handling
const extractMediaFilename = (message: Message): string => {
  // Try to extract filename from media_meta or body
  if (message.media_meta) {
    try {
      const meta = typeof message.media_meta === 'string' ? JSON.parse(message.media_meta) : message.media_meta;
      if (meta.filename || meta.originalname) {
        return meta.filename || meta.originalname;
      }
    } catch (e) {
      console.warn('Failed to parse message_meta:', e);
    }
  }
  // Try file_name field
  if (message.file_name) {
    return message.file_name;
  }
  // For documents/messages without file_name, try extracting from body (e.g., "Terms and Conditions VF.pdf")
  if (message.body && message.media_type === 'document') {
    // Body contains just the filename for documents
    return message.body;
  }
  // Try to extract from media_url
  if (message.media_url) {
    const urlParts = message.media_url.split('/');
    return urlParts[urlParts.length - 1];
  }
  return 'media_file';
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getPreferredChatName = (chat: Chat): string => {
  if (chat.is_group) {
    return chat.name || chat.derived_client_name || formatPhoneNumber(normalizePhoneNumber(chat.phone_number));
  }

  if (chat.derived_client_name) return chat.derived_client_name;
  if (chat.name && !chat.name.startsWith('+')) return chat.name;
  return chat.name || formatPhoneNumber(normalizePhoneNumber(chat.phone_number));
};

const getPreferredChatPhone = (chat: Chat): string => chat.derived_phone_number || chat.phone_number;

interface Message {
  id: string;
  chat_id: string;
  body: string;
  direction: "inbound" | "outbound";
  created_at: string;
  sender?: string;
  message_id?: string; // WhatsApp message ID (formerly wa_message_id)
  is_deleted?: boolean;
  media_type?: string;
  media_path?: string;
  media_url?: string;
  media_meta?: any;
  file_name?: string;
  file_size?: number;
  entity_id?: string;
  entity_name?: string;
  name?: string;
  is_group?: boolean;
  participants?: number;
  chat_contact_name?: string;
  translated_text?: string;
  source_language?: string;
  is_translated?: boolean;
  sent_by?: string | null;
  deleted_from_whatsapp?: boolean;
  deleted_at?: string;
  timestamp?: string;
}

interface ChatMessagesProps {
  chat: Chat;
  entityId?: string;
  onChatIdentityDetected?: (identity: { derived_phone_number?: string | null; derived_client_name?: string | null }) => void;
  userSelectedFromPanel?: boolean;  // True when user explicitly clicked chat from left panel
  zohoLeadBadge?: React.ReactNode;  // Optional Zoho lead status badge to display in header
  broadcastHistory?: Array<{
    recipient_id: number;
    campaign_id: number;
    campaign_name: string;
    message: string;
    sent_at: string;
  }>;
  loadingBroadcastHistory?: boolean;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  chat,
  entityId,
  onChatIdentityDetected,
  userSelectedFromPanel,
  zohoLeadBadge,
  broadcastHistory = [],
  loadingBroadcastHistory = false,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable ref so loadMessages never re-creates just because parent re-renders
  const onChatIdentityDetectedRef = useRef(onChatIdentityDetected);
  onChatIdentityDetectedRef.current = onChatIdentityDetected;
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [enableTranslation, setEnableTranslation] = useState(false); // Default to OFF (for outbound messages)
  const [targetLanguage, setTargetLanguage] = useState("");
  const [refreshingTranslations, setRefreshingTranslations] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  // WhatsApp delete state
  const [whatsappDeleting, setWhatsappDeleting] = useState<Set<string>>(new Set());
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<Message | null>(null);
  const [showBroadcastHistory, setShowBroadcastHistory] = useState(false);
  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // 🏢 MULTI-TENANT SAAS: Track user-initiated chat selection to control auto-scroll behavior
  // Only auto-scroll when user explicitly clicks on a chat in the left panel
  // Track scroll position to restore after page refresh (TODO: implement scroll position restoration)
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const prevMessagesCountRef = useRef(messages.length);

  // Debug: Log chat prop changes
  useEffect(() => {
    if (chat?.chat_id === '0@c.us' || chat?.chat_id === '0@g.us') {
      console.error('🚨 [ChatMessages] INVALID CHAT DETECTED! Chat prop changed to invalid ID:', chat?.chat_id);
    }
  }, [chat]);

  useEffect(() => {
    setShowBroadcastHistory(false);
  }, [chat.chat_id]);

  // Language options for outbound message translation
  const languageOptions = [
    { code: 'zh-cn', name: 'Chinese (Simplified) (简体中文)' },
    { code: 'zh-tw', name: 'Chinese (Traditional) (繁體中文)' },
    { code: 'ta', name: 'Tamil (தமிழ்)' },
    { code: 'hi', name: 'Hindi (हिन्दी)' },
    { code: 'ms', name: 'Bahasa Melayu' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'th', name: 'Thai' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'ar', name: 'Arabic' },
  ];

  // Scroll to bottom helper
  const scrollToBottom = () => {
    // Try multiple scroll methods for reliability
    // Method 1: Scroll the container directly
    const messagesList = document.querySelector('.messages-list');
    if (messagesList) {
      messagesList.scrollTop = messagesList.scrollHeight;
    }

    // Method 2: Scroll into view using ref
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
  };

  // Save scroll position to sessionStorage for restoration after page refresh
  const saveScrollPosition = () => {
    const messagesList = document.querySelector('.messages-list');
    if (messagesList && chat?.chat_id) {
      const scrollKey = `chat_scroll_${chat.chat_id}`;
      sessionStorage.setItem(scrollKey, messagesList.scrollTop.toString());
    }
  };

  // Restore scroll position from sessionStorage (called after messages load)
  const restoreScrollPosition = () => {
    if (userSelectedFromPanel) {
      // Don't restore if user just selected this chat - let scroll-to-bottom happen
      return;
    }

    const messagesList = document.querySelector('.messages-list');
    if (messagesList && chat?.chat_id) {
      const scrollKey = `chat_scroll_${chat.chat_id}`;
      const savedPosition = sessionStorage.getItem(scrollKey);
      if (savedPosition) {
        const scrollPosition = parseInt(savedPosition, 10);
        // Only restore if it's not already at bottom (user had scrolled up)
        if (scrollPosition < messagesList.scrollHeight - messagesList.clientHeight - 100) {
          messagesList.scrollTop = scrollPosition;
        }
      }
    }
  };

  const loadMessages = useCallback(async () => {
    if (!chat?.chat_id) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await orchestratorApi.getChatMessages(chat.chat_id, 100000, entityId);
      if (data.chat_identity && onChatIdentityDetectedRef.current) {
        onChatIdentityDetectedRef.current(data.chat_identity);
      }
      // Filter out:
      // 1. Empty sync placeholder messages (no body)
      // 2. Messages deleted from WhatsApp (deleted_from_whatsapp = true)
      const filteredMessages = (data.messages || []).filter((msg: Message) =>
        msg.body && msg.body.trim().length > 0 && !msg.deleted_from_whatsapp
      );
      setMessages(filteredMessages);

      // Auto-trigger translation for untranslated non-English messages (ALWAYS - separate from outbound toggle)
      const untranslatedMessages = filteredMessages.filter((msg: Message) =>
        !msg.is_translated &&
        !msg.translated_text &&
        msg.body &&
        msg.body.length > 1 &&
        !/^\d+$/.test(msg.body)
      );

      if (untranslatedMessages.length > 0) {
        console.log(`[Auto-Translation] Triggering translation for ${untranslatedMessages.length} messages in chat ${chat.chat_id}`);
        // Trigger backend translation for this chat
        triggerTranslationForChat(chat.chat_id);
      }
    } catch (error: any) {
      setError(error.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [chat?.chat_id, entityId]); // onChatIdentityDetected accessed via ref — stable, no loop

  // Trigger translation for a specific chat
  const triggerTranslationForChat = async (chatId: string) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/translate-chat/${chatId}`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
      });
      const data = await response.json();
      console.log('[Auto-Translation] Translation triggered:', data);
    } catch (error) {
      console.error('[Auto-Translation] Failed to trigger translation:', error);
    }
  };

  // Load messages when chat changes
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Auto-translate non-English messages that don't have translations
  useEffect(() => {
    if (messages.length === 0) return;

    const translateUntranslatedMessages = async () => {
      const messagesToTranslate = messages.filter(msg => {
        // Skip if:
        // - Already has translation
        // - Is deleted
        // - Has no body
        // - Body is just numbers or very short
        if (msg.is_translated && msg.translated_text) return false;
        if (msg.deleted_from_whatsapp) return false;
        if (!msg.body || msg.body.trim().length < 3) return false;
        if (/^\d+$/.test(msg.body)) return false; // Just numbers

        // Check if message is likely non-English
        const isEnglish = isLikelyEnglish(msg.body);
        return !isEnglish; // Translate if NOT English
      });

      if (messagesToTranslate.length === 0) return;

      console.log(`[Auto-Translate] Found ${messagesToTranslate.length} untranslated non-English messages`);

      // Translate each message (sequentially to avoid overwhelming the API)
      for (const msg of messagesToTranslate) {
        try {
          const authHeaders = await orchestratorApi.getAuthHeaders();
          const response = await fetch(`/api/translate-message/${msg.id}`, {
            method: 'POST',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json'
            }
          });

          const result = await response.json();

          if (result.ok && result.translatedText) {
            // Update the message in local state with translation
            setMessages(prevMessages =>
              prevMessages.map(m =>
                m.id === msg.id
                  ? {
                      ...m,
                      translated_text: result.translatedText,
                      source_language: result.sourceLanguage,
                      is_translated: true
                    }
                  : m
              )
            );

            console.log(`[Auto-Translate] Translated message ${msg.id}: ${result.sourceLanguage} -> English`);
          }
        } catch (error) {
          console.error(`[Auto-Translate] Failed to translate message ${msg.id}:`, error);
        }

        // Small delay between translations to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    };

    // Run translation check after a short delay (to avoid running on every re-render)
    const timeoutId = setTimeout(() => {
      translateUntranslatedMessages();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Debug: Log messages state changes (keep minimal)
  useEffect(() => {
    prevMessagesCountRef.current = messages.length;
  }, [messages]);

  // Auto-scroll to bottom ONLY when user explicitly selects chat from left panel
  // NOT on:
  // - Real-time message updates (let user control their scrolling)
  // - Translation refresh (maintain position)
  // - Background sync (let user read undisturbed)
  useEffect(() => {
    // Only scroll when user selected this chat explicitly from left panel AND there are messages
    if (messages.length > 0 && !loading && userSelectedFromPanel) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages, loading, userSelectedFromPanel]);

  // 📍 SCROLL POSITION RESTORATION: Save and restore scroll position across page refreshes
  useEffect(() => {
    // Restore scroll position after messages load (only if not a new selection)
    if (messages.length > 0 && !loading && !userSelectedFromPanel) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        restoreScrollPosition();
      }, 50);
    }
  }, [messages, loading, userSelectedFromPanel, chat?.chat_id]);

  // Track scroll position changes and save to sessionStorage
  useEffect(() => {
    const messagesList = document.querySelector('.messages-list');
    if (!messagesList || !chat?.chat_id) return;

    // Save scroll position on scroll event (throttled)
    let scrollTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        saveScrollPosition();
      }, 100); // Throttle to save only after scrolling stops
    };

    messagesList.addEventListener('scroll', handleScroll);

    return () => {
      messagesList.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [chat?.chat_id]);

  // Periodic translation refresh - check for updated translations every 30 seconds
  useEffect(() => {
    if (!chat?.chat_id) return;

    const refreshTranslations = async () => {
      // Only refresh if there are messages that need translation
      const hasTranslatableContent = messages.some(msg =>
        msg.body &&
        !msg.translated_text &&
        msg.body.length > 1 &&
        !/^\d+$/.test(msg.body) // Not just numbers
      );

      if (hasTranslatableContent) {
        setRefreshingTranslations(true);
        try {
          await loadMessages(); // Reload messages to get updated translations
        } finally {
          setRefreshingTranslations(false);
        }
      }
    };

    // Set up periodic refresh every 30 seconds
    const interval = setInterval(refreshTranslations, 30000);

    return () => clearInterval(interval);
  }, [chat?.chat_id, messages, loadMessages]);

  // Listen for new message events and auto-refresh if it's for current chat
  useEffect(() => {
    const handleMessageReceived = (event: CustomEvent) => {
      if (chat?.chat_id && event.detail?.chatId === chat.chat_id) {
        // Don't auto-scroll on new message arrival - let user control their scrolling
        loadMessages();
      }
    };

    // Add event listener
    window.addEventListener('whatsappMessageReceived', handleMessageReceived as EventListener);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('whatsappMessageReceived', handleMessageReceived as EventListener);
    };
  }, [chat?.chat_id, loadMessages]);

  // Listen for message deletion events and auto-refresh if it's for current chat
  useEffect(() => {
    const handleMessageDeleted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        type: string;
        data: {
          chatId: string;
          tenantId: string;
          entityId: string;
          deletedCount: number;
        };
      }>;

      if (chat?.chat_id && customEvent.detail?.data?.chatId === chat.chat_id) {
        console.log('[ChatMessages] Message deletion event received, refreshing messages', {
          chatId: chat.chat_id,
          deletedCount: customEvent.detail.data.deletedCount
        });
        loadMessages();
      }
    };

    // Add event listener for message deletions from WebSocket
    window.addEventListener('message_deleted', handleMessageDeleted);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('message_deleted', handleMessageDeleted);
    };
  }, [chat?.chat_id, loadMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !chat?.chat_id) return;

    setSending(true);
    try {
      let messageToSend = newMessage.trim();

      // If translation is enabled, translate the message first
      if (enableTranslation && targetLanguage) {
        try {
          const authHeaders = await orchestratorApi.getAuthHeaders();
          // Call translation API
          const translationResponse = await fetch(`/api/translate`, {
            method: 'POST',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: messageToSend,
              targetLanguage: targetLanguage,
            }),
          });

          if (translationResponse.ok) {
            const translationResult = await translationResponse.json();
            if (translationResult.ok && translationResult.translatedText) {
              messageToSend = translationResult.translatedText;
            }
          }
        } catch (translationError) {
          console.warn('Translation failed, sending original message:', translationError);
          // Continue with original message if translation fails
        }
      }

      await orchestratorApi.sendMessage({
        request_id: `msg_${Date.now()}`,
        chat_id: chat.chat_id,
        body: messageToSend,
      });
      setNewMessage("");
      // Reload messages after sending
      setTimeout(loadMessages, 1000);
    } catch (error: any) {
      setError(error.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setSelectedMessages(new Set());
  };

  const toggleMessageSelection = (messageId: string) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(messageId)) {
      newSelected.delete(messageId);
    } else {
      newSelected.add(messageId);
    }
    setSelectedMessages(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.size === 0 || !entityId) return;

    if (!confirm(`Are you sure you want to delete ${selectedMessages.size} message(s) from the database? This will mark them as deleted but preserve them for audit purposes.`)) {
      return;
    }

    setDeleting(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/chats/${chat.chat_id}/messages`, {
        method: 'DELETE',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messageIds: Array.from(selectedMessages),
          entityId: entityId,
        }),
      });

      const result = await response.json();

      if (result.ok) {
        alert(`Successfully deleted ${result.deletedCount} message(s)`);
        setDeleteMode(false);
        setSelectedMessages(new Set());
        loadMessages();
      } else {
        throw new Error(result.error || 'Failed to delete messages');
      }
    } catch (error: any) {
      alert(`Error deleting messages: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // WhatsApp Message Deletion Handler
  // ═══════════════════════════════════════════════════════════════════════
  const handleWhatsAppDelete = async (messageId: string, forEveryone: boolean) => {
    if (!entityId || !messageId) {
      alert('Cannot delete message: missing entity ID or message ID');
      return;
    }

    // Get tenantId from Auth0 user
    const tenantId = user?.[`${NAMESPACE}/tenant_id`] as string | undefined;
    if (!tenantId) {
      alert('Cannot delete message: not authenticated or tenant ID not found');
      return;
    }

    // Confirm deletion
    const confirmMsg = forEveryone
      ? 'Delete this message for everyone? This will remove it for all participants in the chat.'
      : 'Delete this message for you? This will only remove it from your view.';
    if (!confirm(confirmMsg)) {
      return;
    }

    // Track this message as being deleted
    setWhatsappDeleting(prev => new Set(prev).add(messageId));

    try {
      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently();
      } catch (authError: any) {
        const message = authError?.message || String(authError);
        if (message.toLowerCase().includes('refresh token')) {
          Object.keys(localStorage)
            .filter(key => key.startsWith('@@auth0spajs@@'))
            .forEach(key => localStorage.removeItem(key));

          alert('Your login session expired. Please sign in again, then retry the delete.');
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname },
            authorizationParams: {
              redirect_uri: window.location.origin,
              prompt: 'login',
            },
          });
          return;
        }

        throw authError;
      }

      const result = await orchestratorApi.deleteWhatsAppMessage(
        tenantId,
        entityId,
        messageId,
        forEveryone,
        accessToken
      );

      if (result.success) {
        alert(result.message || 'Message deleted successfully');
        // Reload messages to reflect the deletion
        loadMessages();
      } else {
        throw new Error(result.error || 'Failed to delete message');
      }
    } catch (error: any) {
      alert(`Error deleting message: ${error.message}`);
    } finally {
      // Remove from tracking set
      setWhatsappDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  if (!chat) {
    return (
      <div className="chat-messages-empty">
        <div className="empty-icon">💬</div>
        <h3>No Chat Selected</h3>
        <p>Select a chat to view messages</p>
      </div>
    );
  }

  return (
    <div className="chat-messages-panel">
      {/* Chat Header */}
      <div className="chat-messages-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <h3 style={{ margin: 0 }}>
            {/* Display group indicator and name, or contact name, or formatted phone */}
            {chat.is_group ? (
              <>
                👥 {getPreferredChatName(chat)}
                {chat.participants && ` (${chat.participants})`}
              </>
            ) : (
              <>{getPreferredChatName(chat)}</>
            )}
          </h3>
          {/* Zoho Lead Status Badge */}
          {zohoLeadBadge && <div style={{ marginLeft: '8px' }}>{zohoLeadBadge}</div>}
        </div>
        {!chat.is_group && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginRight: '12px' }}>
            {formatPhoneNumber(normalizePhoneNumber(getPreferredChatPhone(chat)))}
          </div>
        )}
        <div className="chat-header-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => loadMessages()}
            disabled={loading || refreshingTranslations}
            className="control-btn refresh-btn"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              background: loading || refreshingTranslations ? '#e5e7eb' : '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: loading || refreshingTranslations ? 'not-allowed' : 'pointer',
              opacity: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Refresh messages and translations"
          >
            {loading || refreshingTranslations ? "⏳" : "🔄"} Refresh
          </button>
          {!chat.is_group && (
            <button
              type="button"
              onClick={() => setShowBroadcastHistory((current) => !current)}
              className="control-btn broadcast-history-btn"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#92400e',
                background: showBroadcastHistory ? '#fef3c7' : '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Show or hide broadcast history"
            >
              {showBroadcastHistory ? 'Hide history' : `Broadcast history${broadcastHistory.length > 0 ? ` (${broadcastHistory.length})` : ''}`}
            </button>
          )}
          <button
            onClick={toggleDeleteMode}
            disabled={loading}
            className="control-btn delete-btn"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              background: deleteMode ? '#fee2e2' : '#f3f4f6',
              border: `1px solid ${deleteMode ? '#dc2626' : '#d1d5db'}`,
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: deleteMode ? '#dc2626' : 'inherit'
            }}
            title={deleteMode ? "Cancel delete mode" : "Delete messages from database"}
          >
            {deleteMode ? "❌ Cancel" : "🗑️ Delete from DB"}
          </button>
          {deleteMode && (
            <button
              onClick={handleBulkDelete}
              disabled={deleting || selectedMessages.size === 0}
              className="control-btn confirm-delete-btn"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                background: '#dc2626',
                border: '1px solid #991b1b',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: 'white'
              }}
              title={`Delete ${selectedMessages.size} selected message(s)`}
            >
              {deleting ? "⏳ Deleting..." : `✓ Delete ${selectedMessages.size} msg(s)`}
            </button>
          )}
          {/* QUALIFIED STATUS BADGES TEMPORARILY DISABLED */}
          {/* {chat.status === 'qualified' && (
            <span className="status-badge status-qualified">
              {chat.status}
            </span>
          )} */}
        </div>
      </div>

      {!chat.is_group && showBroadcastHistory && (
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e5e7eb',
          background: '#fffbeb'
        }}>
          {loadingBroadcastHistory ? (
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Loading broadcast history...</div>
          ) : broadcastHistory.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#6b7280' }}>No broadcast messages sent to this chat yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
              {broadcastHistory.map((entry) => (
                <div
                  key={entry.recipient_id}
                  style={{
                    background: 'white',
                    border: '1px solid #fde68a',
                    borderRadius: '6px',
                    padding: '8px 10px'
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px' }}>
                    {entry.campaign_name} • {new Date(entry.sent_at).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {entry.message}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages List */}
      <div className="messages-list">
        {loading && <div className="loading">Loading messages...</div>}
        {error && <div className="error">Error: {error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div className="empty-state">No messages yet</div>
        )}
        {messages.map((message) => {
          // Format date and time with day of week
          const messageDate = new Date(message.created_at);
          const formattedDateTime = messageDate.toLocaleString('en-US', {
            weekday: 'short',  // Add day of week (e.g., "Tue", "Wed")
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });

          // Check if message was deleted from WhatsApp
          const isDeletedFromWhatsApp = message.deleted_from_whatsapp === true;

          // Get display name for footer
          let footerLeftText = '';
          if (message.direction === "outbound") {
            // For outbound: show entity phone number
            footerLeftText = message.entity_id ? formatPhoneNumber(normalizePhoneNumber(message.entity_id)) : 'Unknown Entity';
          } else {
            // For inbound: show chat name or group name
            if (message.is_group) {
              footerLeftText = message.name || message.chat_contact_name || 'Group Chat';
            } else {
              footerLeftText = message.name || message.chat_contact_name || 'Contact';
            }
          }

          // Determine if translation is available
          const hasTranslation = message.is_translated &&
                                 message.translated_text &&
                                 message.source_language !== 'English' &&
                                 message.translated_text !== message.body;

          return (
            <div
              key={message.id}
              className={`message ${message.direction === "outbound" ? "message-sent" : "message-received"} ${isDeletedFromWhatsApp ? "message-deleted" : ""}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}
            >
            {deleteMode && (
              <input
                type="checkbox"
                checked={selectedMessages.has(message.id)}
                onChange={() => toggleMessageSelection(message.id)}
                style={{
                  marginTop: '8px',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px',
                  flexShrink: 0
                }}
                title={`Select message to delete: ${message.body?.substring(0, 50)}...`}
              />
            )}
            <div className={`message-bubble ${isDeletedFromWhatsApp ? "message-bubble-deleted" : ""}`}>
              {/* Language indicator badge */}
              {hasTranslation && message.source_language && (
                <div className="language-badge">
                  {message.source_language}
                </div>
              )}

              {/* Sender tag for outbound messages */}
              {message.direction === "outbound" && message.sent_by && (
                <div style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  background: message.sent_by === 'ai_agent'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {message.sent_by === 'ai_agent' ? '🤖 AI Agent' : '👤 Salesperson'}
                </div>
              )}

              {/* Media content (images, documents, etc.) — fetched with auth, see AuthedMedia */}
              {message.media_type && (
                <div className="message-media">
                  {(message.media_type.startsWith('image/') ||
                    message.media_type.startsWith('video/') ||
                    message.media_type.startsWith('audio/')) ? (
                    <AuthedMedia
                      filename={message.media_path || extractMediaFilename(message)}
                      mediaType={message.media_type}
                      fileName={message.file_name}
                    />
                  ) : (
                    <div className="message-document">
                      <div className="document-icon">📄</div>
                      <div className="document-info">
                        <div className="document-name">{extractMediaFilename(message)}</div>
                        <div className="document-size">
                          {message.file_size ? formatFileSize(message.file_size) : 'Document'}
                        </div>
                      </div>
                      <button
                        className="download-button"
                        onClick={async () => {
                          const filename = extractMediaFilename(message);
                          const ok = await downloadAuthedMedia(filename);
                          if (!ok) {
                            alert(`The file "${filename}" is not available. This may be because:\n\n1. The media file was not saved when the message was sent\n2. The file has expired or been deleted\n\nPlease contact support if you need this file.`);
                          }
                        }}
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Message body - show both original and translated text when available */}
              {isDeletedFromWhatsApp ? (
                /* Deleted message - show placeholder text */
                <div className="message-body message-deleted-text">
                  <span className="deleted-icon">🗑️</span>
                  <span className="deleted-label">message deleted manually</span>
                </div>
              ) : message.media_type && message.media_type === 'document' ? (
                /* For documents, the body is the filename which is already shown in the document card - skip duplicate text */
                null
              ) : hasTranslation ? (
                <React.Fragment>
                  {/* Original text */}
                  <div className="message-original">
                    <div className="original-label">Original ({message.source_language || 'Detected'}):</div>
                    <div className="message-body original-text">{message.body}</div>
                  </div>

                  {/* Translated text */}
                  <div className="message-translated">
                    <div className="translated-label">English Translation:</div>
                    <div className="message-body translated-text">{message.translated_text}</div>
                  </div>
                </React.Fragment>
              ) : (
                /* Only original text when no translation */
                <div className="message-body">{message.body}</div>
              )}

              <div className="message-meta">
                <span className="message-time">
                  {new Date(message.created_at).toLocaleTimeString()}
                </span>
                {message.direction === "outbound" && (
                  <span className="message-sent">✓</span>
                )}
              </div>

              {/* ═══════════════════════════════════════════════════════════════════════
                  WhatsApp Delete Modal
                  ═══════════════════════════════════════════════════════════════════════ */}
              {showDeleteModal && messageToDelete && (
                <div className="delete-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                  <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                    <h3>Delete Message</h3>
                    <p>Outbound messages are deleted for everyone.</p>
                    <div className="delete-options">
                      <button
                        onClick={() => {
                          handleWhatsAppDelete(messageToDelete.message_id!, true);
                          setShowDeleteModal(false);
                        }}
                        className="delete-option-btn delete-for-everyone-btn"
                        disabled={whatsappDeleting.has(messageToDelete.message_id!)}
                      >
                        <div className="delete-option-icon">🗑️</div>
                        <div className="delete-option-text">
                          <strong>Delete for Everyone</strong>
                          <span>Remove it for all participants in the chat</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="delete-option-btn cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="message-footer">
                <span className="message-footer-left">{footerLeftText}</span>
                <span className="message-footer-right">
                  {formattedDateTime}
                  {/* Delete icon - only show if message has message_id */}
                  {message.message_id && !isDeletedFromWhatsApp && (
                    <span
                      className="delete-icon"
                      onClick={() => {
                        setMessageToDelete(message);
                        setShowDeleteModal(true);
                      }}
                      title="Delete message"
                    >
                      🗑️
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Box */}
      <div className="chat-reply-container">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="reply-input"
          rows={3}
          disabled={sending}
        />
        <div className="reply-controls">
          <div className="translation-controls">
            <label className="translation-toggle">
              <input
                type="checkbox"
                checked={enableTranslation}
                onChange={(e) => setEnableTranslation(e.target.checked)}
              />
              <span>🌐 Translate outbound:</span>
            </label>
            {enableTranslation && (
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="language-select"
              >
                <option value="">-- Select language --</option>
                {languageOptions.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            onClick={handleSendMessage}
            disabled={sending || !newMessage.trim() || (enableTranslation && !targetLanguage)}
            className="btn-send"
          >
            {sending ? "Sending..." : "💬 Send"}
          </button>
        </div>
      </div>

      <style>{`
        .chat-messages-panel {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .chat-messages-header {
          padding: 1rem;
          border-bottom: 1px solid #e5e5e5;
          background: #f9f9f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .chat-messages-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .chat-header-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-qualified {
          background: #d1fae5;
          color: #065f46;
        }

        .status-unqualified {
          background: #fee2e2;
          color: #991b1b;
        }

        .messages-list {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .message {
          display: flex;
          max-width: 70%;
        }

        .message-received {
          align-self: flex-start;
        }

        .message-sent {
          align-self: flex-end;
        }

        .message-bubble {
          background: #f3f4f6;
          padding: 0.75rem 1rem;
          border-radius: 1rem;
          max-width: 100%;
        }

        .message-sent .message-bubble {
          background: #3b82f6;
          color: white;
        }

        .message-body {
          margin-bottom: 0.25rem;
          word-wrap: break-word;
        }

        .message-meta {
          font-size: 0.75rem;
          opacity: 0.7;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .message-footer {
          font-size: 0.65rem;
          opacity: 0.6;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.25rem;
          padding-top: 0.25rem;
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }

        .message-sent .message-footer {
          border-top: 1px solid rgba(255, 255, 255, 0.3);
        }

        .message-footer-left {
          font-weight: 500;
        }

        .message-footer-right {
          font-size: 0.6rem;
          color: #ec4899;
          opacity: 1;
        }

        .message-sent .message-footer-right {
          color: #fef08a;
        }

        .language-badge {
          display: inline-block;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 0.15rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.65rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-transform: capitalize;
        }

        .message-sent .language-badge {
          background: rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.9);
        }

        .original-text-toggle {
          margin-top: 0.5rem;
          font-size: 0.75rem;
        }

        .original-text-label {
          cursor: pointer;
          color: #6b7280;
          font-size: 0.7rem;
          opacity: 0.7;
          user-select: none;
        }

        .original-text-label:hover {
          opacity: 1;
        }

        .message-sent .original-text-label {
          color: rgba(255, 255, 255, 0.8);
        }

        .original-text {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 0.25rem;
          font-style: italic;
          opacity: 0.8;
        }

        .message-sent .original-text {
          background: rgba(255, 255, 255, 0.1);
        }

        /* ═══════════════════════════════════════════════════════════════════════
            Delete Icon in Footer
            ═══════════════════════════════════════════════════════════════════════ */
        .delete-icon {
          margin-left: 8px;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.2s;
          font-size: 0.7rem;
        }

        .delete-icon:hover {
          opacity: 1;
        }

        /* ═══════════════════════════════════════════════════════════════════════
            Delete Modal
            ═══════════════════════════════════════════════════════════════════════ */
        .delete-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .delete-modal {
          background: white;
          borderRadius: 12px;
          padding: 1.5rem;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .delete-modal h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.25rem;
          color: #1f2937;
        }

        .delete-modal p {
          margin: 0 0 1rem 0;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .delete-options {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .delete-option-btn {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .delete-option-btn:hover:not(:disabled) {
          background: #f9fafb;
        }

        .delete-option-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .delete-option-icon {
          font-size: 1.5rem;
        }

        .delete-option-text {
          flex: 1;
        }

        .delete-option-text strong {
          display: block;
          color: #1f2937;
          font-size: 0.95rem;
        }

        .delete-option-text span {
          display: block;
          color: #6b7280;
          font-size: 0.8rem;
          margin-top: 0.15rem;
        }

        .delete-for-everyone-btn {
          border-color: #dc2626;
          background: #fef2f2;
        }

        .delete-for-everyone-btn:hover:not(:disabled) {
          background: #fee2e2;
        }

        .delete-for-me-btn {
          border-color: #9ca3af;
        }

        .cancel-btn {
          justify-content: center;
          font-weight: 600;
        }

        .chat-reply-container {
          padding: 1rem;
          border-top: 1px solid #e5e5e5;
          background: #f9f9f9;
        }

        .reply-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .translation-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
        }

        .translation-toggle {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          white-space: nowrap;
        }

        .translation-toggle input[type="checkbox"] {
          margin: 0;
        }

        .language-select {
          padding: 0.25rem 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.875rem;
          min-width: 200px;
        }

        .language-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        .reply-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          resize: none;
          font-family: inherit;
        }

        .reply-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        
        .btn-send {
          padding: 0.5rem 1.5rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-send:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-send:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .loading, .error, .empty-state {
          padding: 2rem;
          text-align: center;
          color: #6b7280;
        }

        .error {
          color: #dc2626;
        }

        .chat-messages-empty {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          color: #6b7280;
        }

        .chat-messages-empty .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        /* Media styles */
        .message-media {
          margin: 0.5rem 0;
          border-radius: 8px;
          overflow: hidden;
        }

        .message-image {
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .message-image:hover {
          transform: scale(1.02);
        }

        .message-video {
          width: 100%;
        }

        .message-audio {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.5rem;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
          gap: 0.5rem;
        }

        .message-audio-player {
          width: 100%;
          max-width: 300px;
        }

        .audio-filename {
          font-size: 0.75rem;
          color: #666;
          text-align: center;
          word-break: break-all;
        }

        .message-document {
          display: flex;
          align-items: center;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
          gap: 1rem;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .document-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .document-info {
          flex-grow: 1;
          min-width: 0;
        }

        .document-name {
          font-weight: 500;
          margin-bottom: 0.25rem;
          word-break: break-word;
        }

        .document-size {
          font-size: 0.75rem;
          color: #666;
        }

        .download-button {
          padding: 0.5rem 1rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s ease;
          white-space: nowrap;
        }

        .download-button:hover {
          background: #2563eb;
        }

        /* Media styles for received messages */
        .message-received .message-document {
          background: rgba(0, 0, 0, 0.03);
        }

        .message-received .download-button {
          background: #059669;
        }

        .message-received .download-button:hover {
          background: #047857;
        }

        /* Translation styles */
        .message-original,
        .message-translated {
          margin: 0.5rem 0;
        }

        .original-label,
        .translated-label {
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
          opacity: 0.8;
        }

        .original-label {
          color: #059669; /* Green for original */
        }

        .translated-label {
          color: #2563eb; /* Blue for translation */
        }

        .original-text {
          font-style: italic;
          opacity: 0.9;
          padding: 0.25rem 0;
        }

        .translated-text {
          font-weight: 500;
        }

        /* Slight separation between original and translated */
        .message-original {
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          padding-bottom: 0.5rem;
        }

        .message-translated {
          padding-top: 0.5rem;
        }

        /* Deleted message styles */
        .message-deleted {
          opacity: 0.6;
        }

        .message-bubble-deleted {
          background: #f3f4f6 !important;
          border: 1px dashed #9ca3af;
          opacity: 0.7;
        }

        .message-sent .message-bubble-deleted {
          background: #e5e7eb !important;
          color: #6b7280 !important;
        }

        .message-deleted-text {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-style: italic;
          font-size: 0.875rem;
          color: #9ca3af;
        }

        .deleted-icon {
          font-size: 1rem;
          opacity: 0.7;
        }

        .deleted-label {
          font-weight: 400;
        }

        .message-sent .deleted-label {
          color: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default ChatMessages;
